import type { JsonValue } from "./types.ts";

/**
 * Primitive validators for loading documents. Each takes the path of the value for the error message, so a
 * failure names exactly where in the file the problem is.
 */

const MAX_U32 = 0xffffffff;
const FORBIDDEN_KEY = "__proto__";

/**
 * Asserts a value is a plain object.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The value as a record.
 * @throws Error when the value is not a plain object.
 */
export function asRecord(value: unknown, path: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Asserts a value is an array.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The array.
 * @throws Error when the value is not an array.
 */
export function asArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${path} must be an array`);
  }
  return value;
}

/**
 * Asserts a value is a string.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The string.
 * @throws Error when the value is not a string.
 */
export function asString(value: unknown, path: string): string {
  if (typeof value !== "string") {
    throw new Error(`${path} must be a string`);
  }
  return value;
}

/**
 * Asserts a value is a boolean.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The boolean.
 * @throws Error when the value is not a boolean.
 */
export function asBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`${path} must be a boolean`);
  }
  return value;
}

/**
 * Asserts a value is a finite number.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The number.
 * @throws Error when the value is not a finite number.
 */
export function asNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${path} must be a finite number`);
  }
  return value;
}

/**
 * Asserts a value is an integer.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The integer.
 * @throws Error when the value is not an integer.
 */
export function asInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new Error(`${path} must be an integer`);
  }
  return value;
}

/**
 * Asserts a value is an integer of at least one.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The integer.
 * @throws Error when the value is not a positive integer.
 */
export function asPositiveInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 1) {
    throw new Error(`${path} must be a positive integer`);
  }
  return value;
}

/**
 * Asserts a value is an integer within an inclusive range.
 *
 * @param value Candidate value.
 * @param max Inclusive upper bound; the lower bound is zero.
 * @param path Path for error messages.
 * @returns The integer.
 * @throws Error when the value is outside `[0, max]`.
 */
export function asIntegerInRange(value: unknown, max: number, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > max) {
    throw new Error(`${path} must be an integer in [0, ${max}]`);
  }
  return value;
}

/**
 * Asserts a value is an unsigned 32-bit integer.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The integer.
 * @throws Error when the value is outside `[0, 2^32)`.
 */
export function asUint32(value: unknown, path: string): number {
  return asIntegerInRange(value, MAX_U32, path);
}

/**
 * Asserts every id in a list is unique.
 *
 * @param ids Ids in document order.
 * @param path Path of the list, used with the index of the first duplicate.
 * @throws Error naming the first duplicate.
 */
export function assertUniqueIds(ids: readonly string[], path: string): void {
  const seen = new Set<string>();
  ids.forEach((id, index) => {
    if (seen.has(id)) {
      throw new Error(`${path}[${index}].id ${JSON.stringify(id)} is a duplicate`);
    }
    seen.add(id);
  });
}

/**
 * Returns a copy of a JSON record with keys sorted recursively, so equal records serialise identically.
 * Prototype-manipulating keys are rejected rather than silently applied to the copy.
 *
 * @param value Record to normalise.
 * @param path Path for error messages.
 * @returns A prototype-free copy with sorted keys.
 * @throws Error when a key is `__proto__`.
 */
export function sortedRecord(value: { readonly [key: string]: JsonValue }, path: string): { readonly [key: string]: JsonValue } {
  const out: Record<string, JsonValue> = Object.create(null) as Record<string, JsonValue>;
  for (const key of Object.keys(value).sort()) {
    if (key === FORBIDDEN_KEY) {
      throw new Error(`${path}.${FORBIDDEN_KEY} is not an allowed key`);
    }
    out[key] = sortedJson(value[key] as JsonValue, `${path}.${key}`);
  }
  return out;
}

/**
 * Normalises a JSON value recursively, sorting object keys.
 *
 * @param value Any JSON value.
 * @param path Path for error messages.
 * @returns Normalised value.
 */
export function sortedJson(value: JsonValue, path: string): JsonValue {
  if (Array.isArray(value)) {
    return value.map((item, index) => sortedJson(item as JsonValue, `${path}[${index}]`));
  }
  if (value !== null && typeof value === "object") {
    return sortedRecord(value as { readonly [key: string]: JsonValue }, path);
  }
  return value;
}

/**
 * Asserts a value is a JSON object and returns it normalised.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The record with sorted keys.
 * @throws Error when the value is not a plain object or contains a forbidden key.
 */
export function asJsonRecord(value: unknown, path: string): { readonly [key: string]: JsonValue } {
  return sortedRecord(asRecord(value, path) as { readonly [key: string]: JsonValue }, path);
}

/**
 * Asserts a value is a finite number on a `1/steps` lattice, the form of a prop position quantised to
 * pixels (ADR-027).
 *
 * @param value Value to check.
 * @param steps Lattice steps per unit, the tile size in pixels.
 * @param path Path for the error message.
 * @returns The number.
 * @throws Error when the value is not a finite multiple of `1/steps`.
 */
export function asQuantised(value: unknown, steps: number, path: string): number {
  const number = asNumber(value, path);
  if (!Number.isInteger(number * steps)) {
    throw new Error(`${path} must be a multiple of 1/${steps}`);
  }
  return number;
}
